import AdvancingSessionsPanel from "advancingapprove/list/AdvancingSessionsPanel";
import AreYouSureModal from "components/modals/AreYouSureModal";
import GeneralOptionsPanel from "components/optionspanel/GeneralOptionsPanel";
import DocumentFromTemplatePanel from "DocumentFromTemplate/app";
import EmailsPanel from "email/list/App";
import { ExclusivityPanel } from "exclusivityclause/AppExclusivityPanel";
import { ExclusivitySidePanel } from "exclusivityclause/ExcusivitySidePanel";
import { ExclusivityCheckFields } from "exclusivityclause/utils";
import TwoColumnLayout from "layout/TwoColumnLayout";
import { LinkedTravel } from "linkedtravel";
import moment from "moment";
import NotesPanel from "note/list/app";
import { getProductsByParentQueryKey } from "eSign/panelsegment/ProductsPanel";
import ProductsPanel from "Products/ProductsPanel";
import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import LinkedDocumentPanel from "show/components/linkeddocumentpanel/LinkedDocumentPanel";
import LinkedInvoicePanel from "show/components/linkeddocumentpanel/LinkedInvoicePanel";
import FilesPanel from "show/files/app";
import { ShowItinerarySidebarApp } from "show/ItinerarySidebar/app";
import TimetablePanel from "timetable";
import UserTasksPanel from "userTask/list/app";
import { type UploadFilesConfig, uploadFilesToCoreApi } from "utilities/fileUploadUtils";
import { isPanelVisible } from "utilities/panelUtils";
import { routes } from "utilities/routing/generatedRoutes";
import ShowActivityPanel from "show/detail/activity/App";
import { useFreefieldState } from "shared/utils/freeFields/hooks";
import { ShowDetailSection } from "./ShowDetailSection";
import { ShowFormState, mapShowToShowFormState } from "shared/utils/fieldsHelper/showFields";
import { getFreefieldId, isFreefieldKey } from "shared/utils/freeFields/utils";
import { useLoaderData } from "react-router";
import { toCompanySmall } from "shared/utils/companyUtils";
import GuestlistPanel from "guestlist/app";

const ShowDetailApp = () => {
  const viewModel = useLoaderData<server.viewmodel.ShowDetailFrontModel>();
  const {
    exclusivityClause,
    linkedContractPanelConfig,
    linkedFilePanelConfig,
    linkedNotePanelConfig,
    linkedEmailPanelConfig,
    linkedQuotePanelConfig,
    linkedInvoicePanelConfig,
    linkedProductPanelConfig,
    timetablePanelConfig,
    linkedUserTaskPanelConfig,
    linkedPurchaseAgreementPanelConfig,
    guestlistPanelConfig,
    advancingPanelConfig,
    logisticsPanelConfig,
    itinerarySidebarPanel,
    generateDocumentPanelConfig,
    showExclusivityPanel,
    activityPanelConfig
  } = viewModel;

  const vm = viewModel;

  const [show, setShow] = React.useState<server.dto.Show>(vm.show);
  const [showInfo, setShowInfo] = React.useState<ShowFormState>(mapShowToShowFormState(vm.show));
  const [tags, setTags] = React.useState<server.dto.TagSmall[]>(vm.show.tags || []);
  const [promoterCompany, setPromoterCompany] = React.useState<server.dto.Company>(vm.promoterCompany);
  const [promoterContact, setPromoterContact] = React.useState<server.dto.Contact>(vm.promoterContact);
  const [venueContact, setVenueContact] = React.useState<server.dto.Contact>(vm.venueContact);
  const [hotelContact, setHotelContact] = React.useState<server.dto.Contact>(vm.hotelContact);

  const ffState = useFreefieldState(vm.show.freeFields, vm.freeFieldCategories, vm.freeFieldDefinitionPermissions);

  const urlFreeFieldIds = React.useMemo(() => new Set(
    vm.freeFieldCategories.flatMap(cat => cat.definitions.filter(def => def.type === "url").map(def => `${def.freeFieldDefinitionId}`))
  ), [vm.freeFieldCategories]);

  const queryClient = useQueryClient();

  const [displayLockModal, setDisplayLockModal] = React.useState(false);
  const [displayUnlockModal, setDisplayUnlockModal] = React.useState(false);
  const [displayDeleteModal, setDisplayDeleteModal] = React.useState(false);

  const refreshShow = async () => {
    const res = await window.CoreApi.Show.GetSidePanelData(show.showId, { toastOnError: true });
    if (res.data.hasErrors) return;

    const sidePanelData = res.data.data;
    setShow(sidePanelData.show);
    setShowInfo(mapShowToShowFormState(sidePanelData.show));
    setTags(sidePanelData.show.tags || []);
    setPromoterCompany(sidePanelData.promoterCompany);
    setPromoterContact(sidePanelData.promoterContact);
    setVenueContact(sidePanelData.venueContact);
    setHotelContact(sidePanelData.hotelContact);
  };

  const onValueChanged = async (key: string, value: any) => {
    if (isFreefieldKey(key)) {
      ffState.setEditableFreefieldValue(getFreefieldId(key), value);
      return;
    } if (key === "tags") {
      setTags(value);
      return;
    }
    else if (key === "artist") {
      setShowInfo(prev => ({ ...prev, artist: value, attendees: [] }));
      return;
    }
    else if (key === "venue") {
      setShowInfo(prev => ({ ...prev, venue: value, venueContact: null, capacity: null, doors: { open: null, close: null }, ageRestriction: null }));
      setVenueContact(null);
      return;
    }
    else if (key === "promoter") {
      setShowInfo(prev => ({ ...prev, promoter: toCompanySmall(value), promoterContact: null }));
      setPromoterContact(null);
      setPromoterCompany(value);
      return;
    }
    else if (key === "hotel") {
      setShowInfo(prev => ({ ...prev, hotel: value, hotelContact: null }));
      setHotelContact(null);
      return;
    }
    else if (key === "ageRestriction") {
      setShowInfo(prev => ({ ...prev, ageRestriction: value?.value ?? value }));
      return;
    }
    else if (key === "currencyAndExchangeRate") {
      setShowInfo(prev => ({ ...prev, currency: value.currency, exchangeRate: value.exchangeRate }));
      return;
    }
    else if (key === "promoterContact") {
      setPromoterContact(value);
      setShowInfo(prev => ({ ...prev, promoterContact: value }));
      return;
    }
    else if (key === "hotelContact") {
      setHotelContact(value);
      setShowInfo(prev => ({ ...prev, hotelContact: value }));
      return;
    }
    else if (key === "venueContact") {
      setVenueContact(value);
      setShowInfo(prev => ({ ...prev, venueContact: value }));
      return;
    }
    else {
      // generic showInfo field update
      setShowInfo(prev => ({ ...prev, [key]: value }));
    }
  };

  const onSuccessfulUpdate = async (key: string, newValue: any) => {
    // Reformat URL if it's a free field URL input
    if (urlFreeFieldIds.has(key)) {
      const result = await window.CoreApi.Url.Reformat(newValue);
      ffState.setEditableFreefieldValue(parseInt(key), result.data.data);
    }

    if (isFreefieldKey(key)) {
      return;
    }
    else if (key === "bookkeeping" || key === "currencyAndExchangeRate") {
      queryClient.invalidateQueries({ queryKey: getProductsByParentQueryKey("show", show.showId) });
    }
    else if (key === "promoterContact" && newValue != null) {
      window.CoreApi.CompanyToContact.SetLink({ companyId: showInfo.promoter.companyId, contactId: newValue.contactId, isActive: true });
    } else if (key === "hotelContact" && newValue != null) {
      window.CoreApi.CompanyToContact.SetLink({ companyId: showInfo.hotel.companyId, contactId: newValue.contactId, isActive: true });
    } else if (key === "venueContact" && newValue != null) {
      window.CoreApi.CompanyToContact.SetLink({ companyId: showInfo.venue.venueId, contactId: newValue.contactId, isActive: true });
    }

    await refreshShow();
  };

  const exclusivityCheckFields: ExclusivityCheckFields = {
    artistId: show.artist?.artistId,
    excludeShowId: show.showId,
    exclusivityClause,
    showStartDisplay: moment.utc(show.dateTime.startDisplay),
    showEndDisplay: moment.utc(show.dateTime.endDisplay),
    venueLocation: show.venue?.location,
    venueId: show.venue?.venueId,
    appointmentPhase: show.status.appointmentPhase,
  };

  const canLock = show.permissions.canLockUnlock && (!show.isLocked);
  const canUnlock = show.permissions.canLockUnlock && (show.isLocked);

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <ExclusivitySidePanel exclusivityCheckFields={exclusivityCheckFields} />

      <ShowDetailSection
        show={show}
        showInfo={showInfo}
        tags={tags}
        promoterCompany={promoterCompany}
        promoterContact={promoterContact}
        venueContact={venueContact}
        hotelContact={hotelContact}
        exclusivityClause={exclusivityClause}
        ffState={ffState}
        onValueChanged={onValueChanged}
        onSuccessfulUpdate={onSuccessfulUpdate}
      />

      {isPanelVisible(timetablePanelConfig) && <TimetablePanel
        showId={show.showId}
        panelConfig={timetablePanelConfig}
      />}

      {isPanelVisible(logisticsPanelConfig) && <LinkedTravel
        show={show}
        canLinkUnlinkTravel={show.permissions.canLinkUnlinkTravel}
        config={logisticsPanelConfig}
      />}

      {isPanelVisible(advancingPanelConfig) && <AdvancingSessionsPanel
        showId={show.showId}
        config={advancingPanelConfig}
      />}

      {isPanelVisible(guestlistPanelConfig) && <GuestlistPanel
        showId={show.showId}
        showDates={showInfo.showDates}
        canActivate={show.permissions.canActivateGuestlist}
        panelConfig={guestlistPanelConfig}
      />}

      {isPanelVisible(linkedUserTaskPanelConfig) && <UserTasksPanel
        mainArea="show"
        config={linkedUserTaskPanelConfig}
      />}

      {isPanelVisible(linkedNotePanelConfig) && <NotesPanel
        mainArea="show"
        config={linkedNotePanelConfig}
      />}

      {isPanelVisible(linkedEmailPanelConfig) && <EmailsPanel
        mainArea="show"
        emailTemplateType="showEmailTemplate"
        config={linkedEmailPanelConfig}
      />}

      {isPanelVisible(generateDocumentPanelConfig) && <DocumentFromTemplatePanel
        itemId={show.showId}
        api={window.CoreApi.Show.ExportOptionsForDetail}
        panelTitle={generateDocumentPanelConfig.headerLabel}
      />}

      {isPanelVisible(linkedFilePanelConfig) && <FilesPanel
        config={linkedFilePanelConfig}
        uploadFiles={(files: File[], uploadConfig: UploadFilesConfig) => {
          return uploadFilesToCoreApi(
            window.CoreApi.File.UploadClientFiles,
            window.CoreApi.File.UploadClientFiles.absoluteUrl(),
            {
              files,
              mainArea: "show",
              parentId: show.showId
            },
            uploadConfig
          );
        }}
        fetchFilesApi={(page, perPage) => window.CoreApi.Show.PaginatedFiles({ parentId: show.showId, page, perPage })}
      />}

      {isPanelVisible(linkedProductPanelConfig) && <ProductsPanel
        id={show.showId}
        type={"show"}
        productsPanelConfig={linkedProductPanelConfig}
      />}

      {isPanelVisible(linkedQuotePanelConfig) && <LinkedDocumentPanel
        config={linkedQuotePanelConfig}
        showId={show.showId}
        bookkeepingId={show.bookkeeping.bookkeepingId}
      />}

      {isPanelVisible(linkedPurchaseAgreementPanelConfig) && <LinkedDocumentPanel
        config={linkedPurchaseAgreementPanelConfig}
        showId={show.showId}
        bookkeepingId={show.bookkeeping.bookkeepingId}
      />}

      {isPanelVisible(linkedContractPanelConfig) && <LinkedDocumentPanel
        config={linkedContractPanelConfig}
        showId={show.showId}
        bookkeepingId={show.bookkeeping.bookkeepingId}
      />}

      {isPanelVisible(linkedInvoicePanelConfig) && <LinkedInvoicePanel
        config={linkedInvoicePanelConfig}
        show={show}
      />}

      {displayLockModal && <AreYouSureModal
        title={RESX.InvoiceExport.Lock}
        yesButtonLabel={RESX.InvoiceExport.Lock}
        yesButtonColor="red"
        onClose={() => { setDisplayLockModal(false); }}
        onYes={async () => window.CoreApi.Show.Lock({ lock: true, showId: show.showId }).then(() => { window.location.reload(); })}
      />}

      {displayUnlockModal && <AreYouSureModal
        title={RESX.InvoiceExport.Unlock}
        yesButtonLabel={RESX.InvoiceExport.Unlock}
        yesButtonColor="red"
        onClose={() => { setDisplayUnlockModal(false); }}
        onYes={async () => window.CoreApi.Show.Unlock({ lock: false, showId: show.showId }).then(() => { window.location.reload(); })}
      />}

      {displayDeleteModal && <AreYouSureModal
        title={RESX.GeneralLabels.Delete}
        textContent={RESX.GeneralWarnings.DeleteSure}
        onClose={() => { setDisplayDeleteModal(false); }}
        onYes={async () => window.CoreApi.Show.Delete(show.showId).then(() => window.location.href = viewModel.listUrl)}
        yesButtonLabel={RESX.GeneralLabels.Delete}
        yesButtonColor="red"
      />}

    </TwoColumnLayout.LeftColumn>
    <TwoColumnLayout.RightColumn size="medium">
      <GeneralOptionsPanel
        data={viewModel.optionsPanel}
        links={[
          { text: RESX.Buttons.ListView, href: viewModel.listUrl, visible: true },
          { text: RESX.InvoiceExport.Lock, visible: canLock, onClick: () => { setDisplayLockModal(true); } },
          { text: RESX.InvoiceExport.Unlock, visible: canUnlock, onClick: () => { setDisplayUnlockModal(true); } },
          { text: RESX.Buttons.btnCopy, visible: show.permissions.canCopy, href: routes.show_Copy(show.showId).absolute },
          { text: RESX.Buttons.btnDelete, visible: show.permissions.canDelete, onClick: () => { setDisplayDeleteModal(true); } }
        ]}
        shortcutOrder={["timetable", "advancing", "guestlist", "task", "note", "email", "file", "product", "quote", "purchaseAgreement", "contract", "invoice"]}
      />

      {isPanelVisible(showExclusivityPanel) && <ExclusivityPanel
        show={show}
        canModifyShow={show.permissions.canModify}
        exclusivityFields={exclusivityCheckFields}
        config={showExclusivityPanel}
      />}

      {(isPanelVisible(itinerarySidebarPanel) && (show.artist != null)) &&
        <ShowItinerarySidebarApp
          showId={show.showId}
          canLinkUnlinkTravel={show.permissions.canLinkUnlinkTravel}
          startDate={moment.utc(show.dateTime.startDisplay)}
          endDate={moment.utc(show.dateTime.endDisplay)}
          artist={show.artist}
          panelConfig={itinerarySidebarPanel}
          disableNewAppointments={false}
        />}

      {isPanelVisible(activityPanelConfig) && <ShowActivityPanel
        showId={show.showId}
        config={activityPanelConfig}
      />}

    </TwoColumnLayout.RightColumn>
  </TwoColumnLayout>;
};

export default ShowDetailApp;
